import React, { Fragment, useRef, useCallback, useState } from "react";
import "./Rider_closed_assignment.css";
import useFetchPastAssignment from "../Libs/useFetchPastAssignment";
import Spinner from "../../utils/spinner";
import { ReactComponent as SearchIcon } from "../../asset/iconamoon_search-light.svg";
import { IoInformationCircleOutline } from "react-icons/io5";
import { FaMotorcycle } from "react-icons/fa6";

function formatCurrency(amount) {
  const formatter = new Intl.NumberFormat("en-NG", {
    style: "currency",
    currency: "NGN",
  });
  return formatter.format(amount);
}

export default function Riderclosedassignment() {
  const {
    data,
    isLoading,
    isError,
    error,
    fetchNextPage,
    isFetchingNextPage,
    hasNextPage,
  } = useFetchPastAssignment();

  const assignments =
    data?.pages?.flatMap((page) => page.riderAssignments) || [];

  const [searchQuery, setSearchQuery] = useState("");
  const [showSearchInfo, setShowSearchInfo] = useState(false);

  const filteredAssignments = assignments.filter((assignment) => {
    const packageData = assignment.package?.[0];
    const receiver = packageData?.receiver || {};
    const state = receiver?.state || "";
    const address = receiver?.address_1 || "";
    const receiverName = receiver?.name || "";
    const phoneNumber = receiver?.phone_number || "";
    const status = packageData?.status?.slice(-1)[0]?.status || "";

    const query = searchQuery.toLowerCase();

    return (
      state.toLowerCase().includes(query) ||
      address.toLowerCase().includes(query) ||
      receiverName.toLowerCase().includes(query) ||
      phoneNumber.toLowerCase().includes(query) ||
      status.toLowerCase().includes(query)
    );
  });

  const observerRef = useRef();
  const lastRiderAssignmentElementRef = useCallback(
    (node) => {
      if (isFetchingNextPage) return;
      if (observerRef.current) observerRef.current.disconnect();

      observerRef.current = new IntersectionObserver((entries) => {
        if (entries[0].isIntersecting && hasNextPage) {
          fetchNextPage();
        }
      });

      if (node) observerRef.current.observe(node);
    },
    [isFetchingNextPage, fetchNextPage, hasNextPage]
  );

  return (
    <Fragment>
      <div className="assignment-details">
        {!isLoading && !isError && assignments.length > 0 && (
          <div className="search-container">
            <div className="search-wrapper">
              <span className="search-input-wrapper">
                <SearchIcon />
                <input
                  type="text"
                  placeholder="Search closed assignments..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                />
                <IoInformationCircleOutline
                  className="info-icon"
                  onMouseEnter={() => setShowSearchInfo(true)}
                  onMouseLeave={() => setShowSearchInfo(false)}
                  onClick={() => setShowSearchInfo(!showSearchInfo)}
                />
              </span>
              {showSearchInfo && (
                <div className="search-info-tooltip">
                  <p className="tooltip-title">Search by:</p>
                  <div className="search-tags">
                    <span className="search-tag">Assignment ID</span>
                    <span className="search-tag">Receiver Name</span>
                    <span className="search-tag">Phone Number</span>
                    <span className="search-tag">Address</span>
                    <span className="search-tag">State</span>
                    <span className="search-tag">Status</span>
                  </div>
                </div>
              )}
              <div className="search-hints">
                <span className="hint-tag">Name</span>
                <span className="hint-tag">Phone</span>
                <span className="hint-tag">Address</span>
                <span className="hint-tag">State</span>
                <span className="hint-tag">Status</span>
              </div>
            </div>
          </div>
        )}
        {isLoading && <Spinner />}
        {isError && <p>Error: {error.message}</p>}
        {!isLoading && !isError && assignments.length === 0 && (
          <div className="empty-state">
            <p style={{ fontFamily: "Montserrat" }}>
              No closed assignments found.
            </p>
            <FaMotorcycle className="empty-icon" />
          </div>
        )}
        {!isLoading &&
          !isError &&
          filteredAssignments.length === 0 &&
          searchQuery && (
            <div className="empty-state">
              <p style={{ fontFamily: "Montserrat" }}>
                No matching assignments found.
              </p>
              <FaMotorcycle className="empty-icon" />
            </div>
          )}
        {!isLoading && !isError && filteredAssignments.length > 0 && (
          <div className="ass-details">
            {filteredAssignments.map((assignment, index) => {
              const isLast =
                index === filteredAssignments.length - 1 && hasNextPage;
              const packageData = assignment.package?.[0];
              const status = packageData?.status?.slice(-1)[0]?.status || "N/A";

              return (
                <div
                  key={assignment._id}
                  className="closed-assignment-card"
                  ref={isLast ? lastRiderAssignmentElementRef : null}
                >
                  <div className="card-header">
                    <h2>ID {assignment._id.slice(0, 8).toUpperCase()}</h2>
                    <span
                      className={`status-badge ${status
                        .toLowerCase()
                        .replace(/\s+/g, "-")}`}
                    >
                      {status}
                    </span>
                  </div>

                  <div className="card-body">
                    <div className="info-section">
                      <h3>Receiver Information</h3>
                      <div className="info-grid">
                        <div className="info-item">
                          <p className="info-label">Full Name</p>
                          <p className="info-value">
                            {packageData?.receiver?.name || "N/A"}
                          </p>
                        </div>
                        <div className="info-item">
                          <p className="info-label">Phone Number</p>
                          <p className="info-value">
                            {packageData?.receiver?.phone_number || "N/A"}
                          </p>
                        </div>
                        <div className="info-item">
                          <p className="info-label">Address</p>
                          <p className="info-value">
                            {packageData?.receiver?.address_1 || "N/A"}
                          </p>
                        </div>
                        <div className="info-item">
                          <p className="info-label">State</p>
                          <p className="info-value">
                            {packageData?.receiver?.state || "N/A"}
                          </p>
                        </div>
                      </div>
                    </div>

                    <div className="info-section">
                      <h3>Package Information</h3>
                      <div className="info-grid">
                        <div className="info-item">
                          <p className="info-label">Items</p>
                          <div className="info-value">
                            {packageData?.items?.map((item) => (
                              <span key={item?._id} className="item-tag">
                                {item?.name} ({item?.quantity})
                              </span>
                            )) || "N/A"}
                          </div>
                        </div>
                        <div className="info-item">
                          <p className="info-label">Shipping Fee</p>
                          <p className="info-value highlight">
                            {packageData?.shipping_cost
                              ? formatCurrency(packageData?.shipping_cost)
                              : "N/A"}
                          </p>
                        </div>
                        <div className="info-item">
                          <p className="info-label">Delivery Date</p>
                          <p className="info-value">
                            {packageData?.estimated_delivery_date
                              ? new Date(
                                  packageData?.estimated_delivery_date
                                ).toLocaleDateString("en-US", {
                                  year: "numeric",
                                  month: "short",
                                  day: "numeric",
                                })
                              : "N/A"}
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
        {isFetchingNextPage && <Spinner />}
      </div>
    </Fragment>
  );
}
